import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/brand/Logo";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { refreshUser } = useAuth();
  const [state, setState] = useState("checking"); // checking | paid | timeout | error
  const [plan, setPlan] = useState(null);
  const tries = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let stop = false;
    const poll = async () => {
      if (stop) return;
      tries.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setPlan(data.plan);
          setState("paid");
          await refreshUser();
          return;
        }
        if (["expired", "failed"].includes(data.payment_status)) { setState("error"); return; }
      } catch { /* keep polling */ }
      if (tries.current >= 10) { setState("timeout"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { stop = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <Shell>
      {state === "checking" && (
        <>
          <Loader2 size={44} className="text-aurum animate-spin mx-auto" />
          <h1 className="mt-5 text-2xl font-bold">Confirming your payment…</h1>
          <p className="mt-2 text-[var(--text-sec)]">This takes just a few seconds.</p>
        </>
      )}
      {state === "paid" && (
        <>
          <CheckCircle2 size={52} className="text-bull mx-auto" />
          <h1 className="mt-5 text-2xl font-bold">Welcome to Aurum {plan === "ELITE" ? "Elite" : "Pro"}! 🎉</h1>
          <p className="mt-2 text-[var(--text-sec)]">Your plan is active. Live data and premium tools are unlocked.</p>
          <Link to="/app" data-testid="go-to-terminal" className="btn-gold mt-7 inline-flex rounded-full px-6 py-2.5 text-sm font-semibold hover:brightness-110 transition-all">Go to terminal</Link>
        </>
      )}
      {state === "timeout" && (
        <>
          <Loader2 size={44} className="text-aurum mx-auto" />
          <h1 className="mt-5 text-2xl font-bold">Still processing…</h1>
          <p className="mt-2 text-[var(--text-sec)]">Your payment is being finalized. It may take a moment to reflect — refresh the terminal shortly.</p>
          <Link to="/app" className="btn-gold mt-7 inline-flex rounded-full px-6 py-2.5 text-sm font-semibold hover:brightness-110 transition-all">Go to terminal</Link>
        </>
      )}
      {state === "error" && (
        <>
          <XCircle size={52} className="text-bear mx-auto" />
          <h1 className="mt-5 text-2xl font-bold">Something went wrong</h1>
          <p className="mt-2 text-[var(--text-sec)]">We couldn't confirm this payment. If you were charged, it will still activate automatically.</p>
          <Link to="/pricing" className="mt-7 inline-flex rounded-full border border-[var(--border2)] px-6 py-2.5 text-sm font-semibold hover:border-aurum hover:text-aurum transition-all">Back to pricing</Link>
        </>
      )}
    </Shell>
  );
}

export function PaymentCancel() {
  return (
    <Shell>
      <XCircle size={52} className="text-[var(--text-muted)] mx-auto" />
      <h1 className="mt-5 text-2xl font-bold">Checkout canceled</h1>
      <p className="mt-2 text-[var(--text-sec)]">No charge was made. You can upgrade anytime.</p>
      <Link to="/pricing" data-testid="back-to-pricing" className="btn-gold mt-7 inline-flex rounded-full px-6 py-2.5 text-sm font-semibold hover:brightness-110 transition-all">Back to pricing</Link>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-[var(--bg)] text-[var(--text)] flex flex-col">
      <div className="h-16 flex items-center px-6 border-b border-[var(--border2)]">
        <Link to="/"><Logo size={26} /></Link>
      </div>
      <div className="flex-1 flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center" data-testid="payment-result">{children}</div>
      </div>
    </div>
  );
}
